import React, { useState, useMemo } from "react";
import { useAuth } from "../lib/auth";
import { Calculator } from "lucide-react";

const TARGETS = {
    // 1.6 g/kg is the muscle-building plateau established by Morton et al. 2018
    // (Br J Sports Med, PMID: 28698222). 0.6 g/lb (~1.3 g/kg) is a practical
    // minimum; 0.7–0.8 g/lb (~1.6–1.8 g/kg) is the sufficient target above
    // which no additional hypertrophy benefit was observed.
    gain: { kcal: "Maintenance (+150–250 if scale stalls)", protein: "1.6 g/kg (0.7–0.8 g/lb) · Morton 2018 plateau", steps: "6,000 / day" },
    recomp: { kcal: "Maintenance", protein: "1.6 g/kg (0.7–0.8 g/lb) · Morton 2018 plateau", steps: "8,000–10,000 / day" },
    fatloss: { kcal: "Maintenance − 300 to 500 kcal", protein: "1.8 g/kg (~0.82 g/lb) · higher during a deficit protects lean mass", steps: "10,000+ / day" },
};

const ACTIVITY = [
    { value: 1.2, label: "Sedentary", desc: "Desk job, little to no exercise" },
    { value: 1.375, label: "Lightly active", desc: "Light exercise 1–3 days / week" },
    { value: 1.55, label: "Moderately active", desc: "Lifting 3–5 days / week (recommended for Hypergains)" },
    { value: 1.725, label: "Very active", desc: "Lifting + lots of walking / cardio 6–7 days / week" },
    { value: 1.9, label: "Extremely active", desc: "Heavy physical job + training" },
];

// Mifflin-St Jeor BMR — most validated equation
function calcBMR({ sex, weightLbs, heightInches, age }) {
    const weightKg = weightLbs / 2.205;
    const heightCm = heightInches * 2.54;
    const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
    return sex === "male" ? base + 5 : base - 161;
}

export default function Nutrition() {
    const { user } = useAuth();
    const target = TARGETS[user.goal || "recomp"];

    // Calculator state
    const [form, setForm] = useState({
        sex: "male",
        age: 30,
        heightInches: 70,
        weightLbs: 180,
        activity: 1.55,
    });
    const [proteinPerLb, setProteinPerLb] = useState(0.75);   // ~1.65 g/kg — Morton 2018 plateau, snaps to slider step
    const [fatPct, setFatPct] = useState(25); // % of calories from fat

    const result = useMemo(() => {
        const bmr = calcBMR({ sex: form.sex, weightLbs: Number(form.weightLbs), heightInches: Number(form.heightInches), age: Number(form.age) });
        const tdee = bmr * Number(form.activity);
        const goal = user.goal || "recomp";
        let targetKcal = tdee;
        if (goal === "fatloss") targetKcal = tdee - 400;
        if (goal === "gain") targetKcal = tdee + 200;

        const proteinG = Math.round(Number(form.weightLbs) * proteinPerLb);
        const proteinKcal = proteinG * 4;
        const fatKcal = Math.round((fatPct / 100) * targetKcal);
        const fatG = Math.round(fatKcal / 9);
        const carbsKcal = Math.max(0, targetKcal - proteinKcal - fatKcal);
        const carbsG = Math.round(carbsKcal / 4);

        return {
            bmr: Math.round(bmr),
            tdee: Math.round(tdee),
            targetKcal: Math.round(targetKcal),
            proteinG, fatG, carbsG,
        };
    }, [form, user.goal, proteinPerLb, fatPct]);

    const Field = ({ label, ...rest }) => (
        <div>
            <label className="label-eyebrow">{label}</label>
            <input className="mt-1 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm" {...rest} />
        </div>
    );

    return (
        <div className="space-y-8" data-testid="nutrition-page">
            <div>
                <div className="label-eyebrow text-accent-red">Fuel the tension</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Nutrition</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Simple, sustainable, evidence-based. No extreme protocols. Your mode is <strong className="text-accent-red">{(user.goal || "recomp").toUpperCase()}</strong>.</p>
            </div>

            {/* Targets */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="tactical-card p-6"><div className="label-eyebrow text-accent-red">Calories</div><p className="mt-2 font-display text-2xl font-black">{target.kcal}</p></div>
                <div className="tactical-card p-6"><div className="label-eyebrow text-accent-red">Protein</div><p className="mt-2 font-display text-2xl font-black">{target.protein}</p></div>
                <div className="tactical-card p-6"><div className="label-eyebrow text-accent-red">Daily steps</div><p className="mt-2 font-display text-2xl font-black">{target.steps}</p></div>
            </div>

            {/* Principles */}
            <div className="tactical-card p-6 space-y-4 text-sm leading-relaxed">
                <h3 className="font-display text-2xl font-black uppercase">Principles</h3>
                <ul className="space-y-2">
                    <li>— <strong>Protein:</strong> 0.6 g/lb (~1.3 g/kg) is the practical minimum. <strong>0.7–0.8 g/lb (~1.6 g/kg)</strong> is the sufficient target — the plateau established by <a href="https://pubmed.ncbi.nlm.nih.gov/28698222/" target="_blank" rel="noopener noreferrer" className="text-accent-red underline underline-offset-2 hover:opacity-80">Morton et al. 2018</a> above which more protein produces no additional muscle gain.</li>
                    <li>— <strong>Carbs and fats:</strong> after protein is hit, fill the remaining calories with whatever mix of carbs and fats you prefer. ~25% calories from fat / rest from carbs is a solid default for performance.</li>
                    <li>— Weigh yourself daily. Use the <strong>weekly average</strong> to make decisions, never a single weigh-in.</li>
                    <li>— If the weekly average is flat for 2–3 weeks and your goal is fat loss, subtract ~100 kcal or add ~1,000 steps. Don't slash 500 kcal out of frustration.</li>
                    <li>— You do not need a large surplus to build muscle. Maintenance works for most lifters for months.</li>
                    <li>— Cardio is optional. Steps cover the base. Add activities you <em>enjoy</em>.</li>
                    <li>— Sleep is a macro. 7+ hours non-negotiable.</li>
                </ul>
            </div>

            {/* Calculator */}
            <div className="tactical-card p-6 space-y-6" data-testid="nutrition-calculator">
                <div className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-accent-red" />
                    <h3 className="font-display text-2xl font-black uppercase">Calorie & Macro Calculator</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                    Estimates your <strong>BMR</strong> (calories burned at rest), <strong>TDEE</strong> (total daily energy expenditure including activity), and a <strong>target intake</strong> based on your selected mode. Uses the validated Mifflin–St Jeor equation. Adjust if the scale tells you otherwise after 2–3 weeks.
                </p>

                {/* Inputs */}
                <div className="grid gap-3 md:grid-cols-2">
                    <div>
                        <label className="label-eyebrow">Sex</label>
                        <div className="mt-1 flex gap-2">
                            {["male", "female"].map(s => (
                                <button key={s} onClick={() => setForm({ ...form, sex: s })} data-testid={`calc-sex-${s}`}
                                    className={`flex-1 rounded-sm border px-3 py-2 text-xs font-bold uppercase tracking-wider ${form.sex === s ? "border-accent-red bg-accent-red text-white" : "border-border bg-card hover:bg-secondary"}`}>{s}</button>
                            ))}
                        </div>
                    </div>
                    <Field label="Age (years)" type="number" min="14" max="100" data-testid="calc-age" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} />
                    <Field label="Height (inches)" type="number" min="48" max="90" data-testid="calc-height" value={form.heightInches} onChange={e => setForm({ ...form, heightInches: e.target.value })} />
                    <Field label="Body weight (lbs)" type="number" min="60" max="600" step="0.5" data-testid="calc-weight" value={form.weightLbs} onChange={e => setForm({ ...form, weightLbs: e.target.value })} />
                </div>

                <div>
                    <label className="label-eyebrow">Activity level</label>
                    <select className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm" data-testid="calc-activity" value={form.activity} onChange={e => setForm({ ...form, activity: parseFloat(e.target.value) })}>
                        {ACTIVITY.map(a => <option key={a.value} value={a.value}>{a.label} — {a.desc}</option>)}
                    </select>
                </div>

                {/* Macro tuning */}
                <div className="grid gap-3 md:grid-cols-2">
                    <div>
                        <label className="label-eyebrow">Protein (g per lb)</label>
                        <input type="range" min="0.6" max="1.0" step="0.05" value={proteinPerLb} onChange={e => setProteinPerLb(parseFloat(e.target.value))} data-testid="calc-protein-slider" className="mt-2 w-full accent-[#ff3b30]" />
                        <div className="text-sm font-bold">{proteinPerLb.toFixed(2)} g/lb {proteinPerLb <= 0.65 && "· practical minimum"} {proteinPerLb >= 0.70 && proteinPerLb <= 0.85 && "· sufficient (Morton 2018)"}</div>
                    </div>
                    <div>
                        <label className="label-eyebrow">Fat (% of calories)</label>
                        <input type="range" min="15" max="40" step="1" value={fatPct} onChange={e => setFatPct(parseInt(e.target.value))} data-testid="calc-fat-slider" className="mt-2 w-full accent-[#ff3b30]" />
                        <div className="text-sm font-bold">{fatPct}%</div>
                    </div>
                </div>

                {/* Results */}
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="border border-border p-4">
                        <div className="label-eyebrow text-muted-foreground">BMR (rest)</div>
                        <div className="font-display text-2xl font-black" data-testid="calc-bmr">{result.bmr} kcal</div>
                    </div>
                    <div className="border border-border p-4">
                        <div className="label-eyebrow text-muted-foreground">TDEE (maintenance)</div>
                        <div className="font-display text-2xl font-black" data-testid="calc-tdee">{result.tdee} kcal</div>
                    </div>
                    <div className="border border-accent-red bg-accent-red/10 p-4">
                        <div className="label-eyebrow text-accent-red">Target ({(user.goal || "recomp").toUpperCase()})</div>
                        <div className="font-display text-2xl font-black" data-testid="calc-target">{result.targetKcal} kcal</div>
                    </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                    <div className="tactical-card p-4">
                        <div className="label-eyebrow text-accent-red">Protein</div>
                        <div className="font-display text-3xl font-black" data-testid="calc-protein">{result.proteinG}g</div>
                        <div className="text-xs text-muted-foreground">{result.proteinG * 4} kcal</div>
                    </div>
                    <div className="tactical-card p-4">
                        <div className="label-eyebrow text-accent-red">Carbs</div>
                        <div className="font-display text-3xl font-black" data-testid="calc-carbs">{result.carbsG}g</div>
                        <div className="text-xs text-muted-foreground">{result.carbsG * 4} kcal</div>
                    </div>
                    <div className="tactical-card p-4">
                        <div className="label-eyebrow text-accent-red">Fat</div>
                        <div className="font-display text-3xl font-black" data-testid="calc-fat">{result.fatG}g</div>
                        <div className="text-xs text-muted-foreground">{result.fatG * 9} kcal</div>
                    </div>
                </div>

                <p className="text-xs text-muted-foreground">
                    Hit protein every day. Carbs and fats can shift around. Use this as a starting point — if your weekly average weight isn't moving the way you want after 2–3 weeks, nudge calories ±100–150 and recheck.
                </p>
            </div>
        </div>
    );
}
